import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LayoutGrid, Loader2, RefreshCw, Table2, Target } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { BudgetPacingBadge } from '@/components/google-ads/budgetPacing';
import {
  getMccInsights,
  getMccOverview,
  getMccPacing,
  type MccAccountCard,
  type MccInsightsCard,
  type MccPacingCard,
  type MccVerdict,
} from '@/services/googleAdsMccService';

// Drill-down target: the existing single-tenant Google Ads console.
const DRILLDOWN_URL = '/ads-tools/kpi-diagnostics?platform=google-ads';

type ViewMode = 'cards' | 'matrix';

const VERDICT_META: Record<MccVerdict, { label: string; className: string }> = {
  ON_TRACK: { label: 'On track', className: 'border-teal/30 bg-teal-light text-teal-dark' },
  WATCH: { label: 'Watch', className: 'border-coral/40 bg-coral-light text-coral-dark' },
  NO_VERDICT: {
    label: 'No verdict',
    className: 'border-border bg-warm-bg text-muted-foreground',
  },
};

function formatUsd(value: number | null | undefined, fractionDigits = 2): string {
  if (value == null) return '—';
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
}

function formatNumber(value: number | null | undefined): string {
  if (value == null) return '—';
  // Conversions can be fractional (Google reports them as floats); show 0-1 dp.
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded)
    ? rounded.toLocaleString()
    : rounded.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function formatPercent(fraction: number | null | undefined): string {
  if (fraction == null) return '—';
  return `${(fraction * 100).toFixed(1)}%`;
}

// Insights cells fall back to "not synced" (matching the mockup) when the
// backend couldn't compute them (no data / too few scored keywords).
function formatQs(insights: MccInsightsCard | undefined, loading: boolean): string {
  if (loading && !insights) return '…';
  if (!insights || insights.avg_quality_score == null) return 'not synced';
  return insights.avg_quality_score.toFixed(1);
}

function formatWasted(insights: MccInsightsCard | undefined, loading: boolean): string {
  if (loading && !insights) return '…';
  if (!insights || insights.wasted_7d_usd == null) return 'not synced';
  return formatUsd(insights.wasted_7d_usd, 0);
}

function pacingBarColor(status: MccPacingCard['pacing_status']): string {
  if (status === 'over_spend' || status === 'over_pacing') return 'bg-coral';
  if (status === 'under_pacing') return 'bg-amber-400';
  return 'bg-teal';
}

function PacingBar({
  pacing,
  loading,
  compact,
}: {
  pacing?: MccPacingCard;
  loading?: boolean;
  compact?: boolean;
}) {
  if (loading && !pacing) {
    return <span className="text-xs text-muted-foreground">Loading…</span>;
  }
  if (!pacing || pacing.pacing_status === 'no_data') {
    return <span className="text-xs italic text-muted-foreground/70">No cap set</span>;
  }
  const pct = pacing.ratio != null ? Math.min(100, Math.round(pacing.ratio * 100)) : null;
  const spendText =
    pacing.budget_usd != null
      ? `${formatUsd(pacing.spent_usd, 0)} / ${formatUsd(pacing.budget_usd, 0)}`
      : null;
  return (
    <div className={compact ? 'flex items-center gap-2' : 'space-y-1'}>
      <div className="flex items-center gap-2">
        <BudgetPacingBadge status={pacing.pacing_status} />
        {spendText ? <span className="text-xs text-muted-foreground">{spendText}</span> : null}
      </div>
      {!compact && pct != null ? (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-border/60">
          <div className={`h-full ${pacingBarColor(pacing.pacing_status)}`} style={{ width: `${pct}%` }} />
        </div>
      ) : null}
    </div>
  );
}

interface MetricProps {
  label: string;
  value: string;
  muted?: boolean;
}

function Metric({ label, value, muted }: MetricProps) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={`truncate text-sm font-semibold ${
          muted ? 'italic text-muted-foreground/70' : 'text-foreground'
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function AccountCard({
  card,
  displayName,
  pacing,
  pacingLoading,
  insights,
  insightsLoading,
  onOpen,
}: {
  card: MccAccountCard;
  displayName: string;
  pacing?: MccPacingCard;
  pacingLoading?: boolean;
  insights?: MccInsightsCard;
  insightsLoading?: boolean;
  onOpen: () => void;
}) {
  const verdict = VERDICT_META[card.verdict] ?? VERDICT_META.NO_VERDICT;
  const m = card.metrics;
  const qsSynced = !!insights && insights.avg_quality_score != null;
  const wastedSynced = !!insights && insights.wasted_7d_usd != null;
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className="cursor-pointer transition-shadow hover:shadow-md focus:outline-none focus:ring-2 focus:ring-teal/40"
    >
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 pb-3">
        <div className="min-w-0">
          <CardTitle className="truncate text-base font-semibold">{displayName}</CardTitle>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            Google Ads
            {card.customer_id ? ` · ${card.customer_id}` : ''}
          </div>
        </div>
        <Badge variant="outline" className={`shrink-0 ${verdict.className}`}>
          {verdict.label}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {card.error ? (
          <div className="rounded border border-border bg-warm-bg px-3 py-2 text-xs text-muted-foreground">
            Not synced — {card.error}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-x-4 gap-y-3">
            <Metric label="Spend" value={formatUsd(m.spend_usd, 0)} />
            <Metric label="Conversions" value={formatNumber(m.conversions)} />
            <Metric label="CPL" value={formatUsd(m.cost_per_conversion_usd)} />
            <Metric label="Avg QS" value={formatQs(insights, !!insightsLoading)} muted={!qsSynced} />
            <Metric label="Wasted 7d" value={formatWasted(insights, !!insightsLoading)} muted={!wastedSynced} />
            <Metric label="CVR" value={formatPercent(m.conversion_rate)} />
            <Metric label="CTR" value={formatPercent(m.ctr)} />
            <Metric label="CPC" value={formatUsd(m.cpc_usd)} />
            <Metric label="Clicks" value={formatNumber(m.clicks)} />
          </div>
        )}
        <div className="border-t border-border/60 pt-2">
          <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Pacing
          </div>
          <PacingBar pacing={pacing} loading={pacingLoading} />
        </div>
      </CardContent>
    </Card>
  );
}


// Skeleton account card — matches AccountCard's shape (header + 3x3 metric
// grid + pacing line) so the grid does not jump when real data lands.
function AccountCardSkeleton() {
  return (
    <Card aria-hidden>
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0 pb-3">
        <div className="min-w-0 flex-1 space-y-1.5">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-3 w-28" />
        </div>
        <Skeleton className="h-5 w-16 shrink-0 rounded-full" />
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <div className="grid grid-cols-3 gap-x-4 gap-y-3">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="space-y-1">
              <Skeleton className="h-2.5 w-12" />
              <Skeleton className="h-4 w-16" />
            </div>
          ))}
        </div>
        <div className="border-t border-border/60 pt-2">
          <Skeleton className="mb-1 h-2.5 w-10" />
          <Skeleton className="h-4 w-32" />
        </div>
      </CardContent>
    </Card>
  );
}

function MatrixRowSkeleton() {
  return (
    <tr aria-hidden className="border-b border-border/60 last:border-0">
      <td className="px-3 py-2">
        <div className="space-y-1">
          <Skeleton className="h-3.5 w-36" />
          <Skeleton className="h-3 w-20" />
        </div>
      </td>
      <td className="px-3 py-2"><Skeleton className="h-5 w-16 rounded-full" /></td>
      {Array.from({ length: 8 }).map((_, i) => (
        <td key={i} className="px-3 py-2 text-right">
          <Skeleton className="ml-auto h-3.5 w-12" />
        </td>
      ))}
      <td className="px-3 py-2"><Skeleton className="h-3.5 w-28" /></td>
    </tr>
  );
}


/**
 * Say what ACTUALLY failed.
 *
 * ⛔ THIS USED TO BE A FIXED STRING: "It requires the Google Ads monthly
 * management flag and agency-operator access." It rendered for ANY failure and
 * asserted two causes it never checked. When an operator hit it, both were
 * already fine — the flag was true in staging and prod, and their role was
 * super_admin. The real cause was a 30s client timeout against a 36s request.
 * They went looking for a flag to flip and a permission to grant, and neither
 * existed. A diagnosis the UI has not verified is worse than no diagnosis.
 */
export function describeMccOverviewError(error: unknown): string {
  const err = error as
    | { code?: string; message?: string; response?: { status?: number; data?: unknown } }
    | undefined;
  const status = err?.response?.status;

  if (err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message ?? '')) {
    return (
      'The account overview timed out. It loads every managed account under the '
      + 'MCC, so it can take a while on a large fleet. Try Refresh, and if it '
      + 'keeps timing out the endpoint needs paginating rather than more waiting.'
    );
  }
  if (status === 401) return 'Your session expired. Sign in again.';
  if (status === 403) {
    return (
      'Your account is not an agency operator, so it cannot read the MCC-wide '
      + 'view. That needs an @agileandco.com login or a platform/expert role.'
    );
  }
  if (status === 503) {
    return (
      'The Google Ads monthly management feature is switched off for this '
      + 'environment (GOOGLE_ADS_MONTHLY_MGMT_ENABLED).'
    );
  }
  const detail =
    typeof (err?.response?.data as { detail?: unknown } | undefined)?.detail === 'string'
      ? ((err?.response?.data as { detail?: string }).detail as string)
      : err?.message;
  return `Could not load the account overview${status ? ` (HTTP ${status})` : ''}${
    detail ? `: ${detail}` : '.'
  }`;
}


export default function MccOverviewPage() {
  const navigate = useNavigate();
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const setCurrentWorkspace = useWorkspaceStore((s) => s.setCurrentWorkspace);
  const selectTenantByWebsite = useWorkspaceStore((s) => s.selectTenantByWebsite);
  const [activeOnly, setActiveOnly] = useState(false);
  const [view, setView] = useState<ViewMode>('cards');

  // website (lowercased) -> workspace, for name display + drill-down switch.
  const workspaceByWebsite = useMemo(() => {
    const map = new Map<string, (typeof workspaces)[number]>();
    for (const ws of workspaces) {
      const key = ws.website?.trim().toLowerCase();
      if (key) map.set(key, ws);
    }
    return map;
  }, [workspaces]);

  // Progressive fleet loading: one monolithic request timed out on a cold
  // cache (every account is a live KPI build), so pages of PAGE_SIZE stream
  // in and render as they land — accounts you can act on immediately while
  // the rest load.
  const PAGE_SIZE = 8;
  const [ovAccounts, setOvAccounts] = useState<MccAccountCard[]>([]);
  const [ovTotal, setOvTotal] = useState<number | null>(null);
  const [ovLoaded, setOvLoaded] = useState(0);
  const [ovPhase, setOvPhase] = useState<'loading' | 'done' | 'error'>('loading');
  const ovRun = useRef(0);

  const loadOverview = useCallback(async (refresh = false) => {
    const run = ++ovRun.current;
    setOvAccounts([]);
    setOvTotal(null);
    setOvLoaded(0);
    setOvPhase('loading');
    let offset = 0;
    try {
      for (;;) {
        const page = await getMccOverview({ activeOnly, refresh, limit: PAGE_SIZE, offset });
        if (run !== ovRun.current) return;
        setOvAccounts((prev) =>
          [...prev, ...page.accounts].sort(
            (a, b) => (b.metrics?.spend_usd ?? 0) - (a.metrics?.spend_usd ?? 0),
          ),
        );
        const total = page.total_count ?? page.account_count;
        setOvTotal(total);
        offset += PAGE_SIZE;
        setOvLoaded(Math.min(offset, total));
        if (!total || offset >= total) break;
      }
      if (run === ovRun.current) setOvPhase('done');
    } catch {
      if (run === ovRun.current) setOvPhase('error');
    }
  }, [activeOnly]);

  useEffect(() => {
    loadOverview(false);
  }, [loadOverview]);

  // Pacing is a separate, heavier operator-gated endpoint fetched in parallel
  // and merged by tenant_id. Not filtered by activeOnly, so no key dependency.
  const pacing = useQuery({
    queryKey: ['mcc-pacing'],
    queryFn: () => getMccPacing(),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const pacingByTenant = useMemo(() => {
    const map = new Map<string, MccPacingCard>();
    for (const p of pacing.data?.accounts ?? []) {
      map.set(p.tenant_id.trim().toLowerCase(), p);
    }
    return map;
  }, [pacing.data]);

  // Insights (Avg QS + Wasted 7d) — separate heavy endpoint, same parallel/merge
  // pattern as pacing.
  const insights = useQuery({
    queryKey: ['mcc-insights'],
    queryFn: () => getMccInsights(),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const insightsByTenant = useMemo(() => {
    const map = new Map<string, MccInsightsCard>();
    for (const i of insights.data?.accounts ?? []) {
      map.set(i.tenant_id.trim().toLowerCase(), i);
    }
    return map;
  }, [insights.data]);

  const nameFor = (tenantId: string) =>
    workspaceByWebsite.get(tenantId.trim().toLowerCase())?.name ?? tenantId;
  const pacingFor = (tenantId: string) => pacingByTenant.get(tenantId.trim().toLowerCase());
  const insightsFor = (tenantId: string) => insightsByTenant.get(tenantId.trim().toLowerCase());

  const openAccount = (tenantId: string) => {
    const ws = workspaceByWebsite.get(tenantId.trim().toLowerCase());
    if (ws) {
      setCurrentWorkspace(ws); // bridges the tenant store via ws.website
    } else {
      // fallback: tenant not in workspace list — selects the explicit
      // unlinked {kind:'tenant'} selection so data still loads
      selectTenantByWebsite(tenantId);
    }
    navigate(DRILLDOWN_URL);
  };

  const accounts = ovAccounts;
  const isRefreshing = ovPhase === 'loading' || pacing.isFetching || insights.isFetching;
  const showInitialSkeletons = ovPhase === 'loading' && accounts.length === 0;
  const SKELETON_COUNT = 6;

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <Target className="h-5 w-5 text-teal" />
            Account Overview
          </h1>
          <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            Every Google Ads account under the agency MCC. Click an account to drill down.
            {ovPhase === 'loading' ? (
              <span className="inline-flex items-center gap-1 text-xs text-coral">
                <Loader2 className="h-3 w-3 animate-spin" /> loading…
              </span>
            ) : null}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {/* Cards / Matrix segmented toggle */}
          <div className="flex rounded-md border border-border p-0.5">
            <button
              type="button"
              onClick={() => setView('cards')}
              className={`flex items-center gap-1 rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                view === 'cards' ? 'bg-teal-light text-teal-dark' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <LayoutGrid className="h-3.5 w-3.5" /> Cards
            </button>
            <button
              type="button"
              onClick={() => setView('matrix')}
              className={`flex items-center gap-1 rounded px-2.5 py-1 text-xs font-medium transition-colors ${
                view === 'matrix' ? 'bg-teal-light text-teal-dark' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Table2 className="h-3.5 w-3.5" /> Matrix
            </button>
          </div>
          <div className="flex items-center gap-2">
            <Switch id="mcc-active-only" checked={activeOnly} onCheckedChange={setActiveOnly} />
            <Label htmlFor="mcc-active-only" className="cursor-pointer text-sm text-muted-foreground">
              Active only
            </Label>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isRefreshing}
            onClick={() => {
              loadOverview(true);
              pacing.refetch();
              insights.refetch();
            }}
          >
            {isRefreshing ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="mr-1 h-3.5 w-3.5" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {ovPhase === 'loading' ? (
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-2 rounded border border-coral/30 bg-coral-light px-3 py-2 text-sm text-coral-dark"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          {ovTotal
            ? `Loading accounts — ${Math.min(ovLoaded, ovTotal)} of ${ovTotal} checked…`
            : 'Loading accounts…'}
        </div>
      ) : null}
      {ovPhase === 'error' ? (
        <div className="flex items-center justify-between rounded border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          <span>
            {accounts.length > 0
              ? `Loading stopped after ${accounts.length} account(s) — the rest failed.`
              : 'Could not load the account overview. It requires the Google Ads monthly management flag and agency-operator access.'}
          </span>
          <Button type="button" variant="outline" size="sm" className="h-7" onClick={() => loadOverview(false)}>
            Retry
          </Button>
        </div>
      ) : null}
      {ovPhase === 'done' && accounts.length === 0 ? (
        <div className="rounded border border-border bg-warm-bg p-3 text-sm text-muted-foreground">
          {activeOnly
            ? 'No accounts had spend in the current window. Turn off “Active only” to see all managed accounts.'
            : 'No managed Google Ads accounts found under the MCC.'}
        </div>
      ) : null}
      {showInitialSkeletons ? (view === 'cards' ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <AccountCardSkeleton key={`sk-card-${i}`} />
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full min-w-[1040px] text-sm">
            <thead>
              <tr className="border-b border-border bg-warm-bg text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Account</th>
                <th className="px-3 py-2 font-medium">Verdict</th>
                <th className="px-3 py-2 text-right font-medium">Spend</th>
                <th className="px-3 py-2 text-right font-medium">Conv</th>
                <th className="px-3 py-2 text-right font-medium">CPL</th>
                <th className="px-3 py-2 text-right font-medium">CVR</th>
                <th className="px-3 py-2 text-right font-medium">CTR</th>
                <th className="px-3 py-2 text-right font-medium">CPC</th>
                <th className="px-3 py-2 text-right font-medium">QS</th>
                <th className="px-3 py-2 text-right font-medium">Wasted 7d</th>
                <th className="px-3 py-2 font-medium">Pacing</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                <MatrixRowSkeleton key={`sk-row-${i}`} />
              ))}
            </tbody>
          </table>
        </div>
      )) : accounts.length > 0 ? (view === 'cards' ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {accounts.map((card) => (
            <AccountCard
              key={card.tenant_id}
              card={card}
              displayName={nameFor(card.tenant_id)}
              pacing={pacingFor(card.tenant_id)}
              pacingLoading={pacing.isLoading}
              insights={insightsFor(card.tenant_id)}
              insightsLoading={insights.isLoading}
              onOpen={() => openAccount(card.tenant_id)}
            />
          ))}
          {ovPhase === 'loading'
            ? Array.from({ length: 3 }).map((_, i) => (
                <AccountCardSkeleton key={`sk-more-${i}`} />
              ))
            : null}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full min-w-[1040px] text-sm">
            <thead>
              <tr className="border-b border-border bg-warm-bg text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Account</th>
                <th className="px-3 py-2 font-medium">Verdict</th>
                <th className="px-3 py-2 text-right font-medium">Spend</th>
                <th className="px-3 py-2 text-right font-medium">Conv</th>
                <th className="px-3 py-2 text-right font-medium">CPL</th>
                <th className="px-3 py-2 text-right font-medium">CVR</th>
                <th className="px-3 py-2 text-right font-medium">CTR</th>
                <th className="px-3 py-2 text-right font-medium">CPC</th>
                <th className="px-3 py-2 text-right font-medium">QS</th>
                <th className="px-3 py-2 text-right font-medium">Wasted 7d</th>
                <th className="px-3 py-2 font-medium">Pacing</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((card) => {
                const verdict = VERDICT_META[card.verdict] ?? VERDICT_META.NO_VERDICT;
                const m = card.metrics;
                return (
                  <tr
                    key={card.tenant_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openAccount(card.tenant_id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openAccount(card.tenant_id);
                      }
                    }}
                    className="cursor-pointer border-b border-border/60 last:border-0 hover:bg-warm-bg/60 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-teal/40"
                  >
                    <td className="px-3 py-2">
                      <div className="font-medium">{nameFor(card.tenant_id)}</div>
                      <div className="text-xs text-muted-foreground">{card.customer_id ?? '—'}</div>
                    </td>
                    <td className="px-3 py-2">
                      <Badge variant="outline" className={verdict.className}>
                        {verdict.label}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right font-medium">
                      {card.error ? '—' : formatUsd(m.spend_usd, 0)}
                    </td>
                    <td className="px-3 py-2 text-right">{card.error ? '—' : formatNumber(m.conversions)}</td>
                    <td className="px-3 py-2 text-right">{card.error ? '—' : formatUsd(m.cost_per_conversion_usd)}</td>
                    <td className="px-3 py-2 text-right">{card.error ? '—' : formatPercent(m.conversion_rate)}</td>
                    <td className="px-3 py-2 text-right">{card.error ? '—' : formatPercent(m.ctr)}</td>
                    <td className="px-3 py-2 text-right">{card.error ? '—' : formatUsd(m.cpc_usd)}</td>
                    <td className="px-3 py-2 text-right">{formatQs(insightsFor(card.tenant_id), insights.isLoading)}</td>
                    <td className="px-3 py-2 text-right">{formatWasted(insightsFor(card.tenant_id), insights.isLoading)}</td>
                    <td className="px-3 py-2">
                      <PacingBar pacing={pacingFor(card.tenant_id)} loading={pacing.isLoading} compact />
                    </td>
                  </tr>
                );
              })}
              {ovPhase === 'loading'
                ? Array.from({ length: 3 }).map((_, i) => (
                    <MatrixRowSkeleton key={`sk-more-row-${i}`} />
                  ))
                : null}
            </tbody>
          </table>
        </div>
      )) : null}
    </div>
  );
}
